import { FC, useEffect, useState, useMemo } from "react";
import useTenant from "@/hooks/useTenant";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import {
  useForm,
  useFieldArray,
  type Resolver,
  type UseFormReturn,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form } from "@capacit-com/lara-law-shared-ui";
import { MetadataEnum } from "@/models/metadata/metadata";

import { MetadataTypesSkeleton } from "./components/MetadataTypesSkeleton";
import { Skeleton } from "@capacit-com/lara-law-shared-ui";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import MetadataFormContent from "./components/MetadataFormContent";
import MetadataFormFooter from "./components/MetadataFormFooter";
import PageContent from "@/pages/PageContent";

export type TenantMetadataFormType = {
  name: string;
  type: "string" | "number" | "boolean" | "dateTime" | "select";
  id?: string | undefined;
  externalPropertyName?: string | null | undefined;
  selectValues?: string[];
};
// Validation schema
const metadataTypeSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, "Navn er påkrævet"),
  externalPropertyName: z.string().optional().or(z.literal("")).or(z.null()),
  type: z.enum(["string", "number", "boolean", "dateTime", "select"]),
  selectValues: z.array(z.string()).optional(),
}) satisfies z.ZodType<TenantMetadataFormType>;

const formSchema = z.object({
  metadataTypes: z.array(metadataTypeSchema),
});

export type FormValues = z.infer<typeof formSchema>;

// Utility function to normalize externalPropertyName values
const normalizeExternalPropertyName = (
  value: string | undefined | null
): string | undefined => {
  return value === "" || value == null ? undefined : value;
};

const MetadataTypesView: FC = () => {
  const [originalData, setOriginalData] = useState<FormValues | null>(null);
  const { updateTenantMetadata, useQueryCurrent, deleteTenantMetadata } =
    useTenant();
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const tenantQuery = useQueryCurrent();
  const [isDeletingMetadata, setIsDeletingMetadata] = useState<
    string | undefined
  >(undefined);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as Resolver<FormValues>,
    mode: "onSubmit",
    defaultValues: {
      metadataTypes: [],
    },
  }) as UseFormReturn<FormValues>;

  const { fields, append } = useFieldArray({
    control: form.control,
    name: "metadataTypes",
  });

  useEffect(() => {
    if (tenantQuery.isFetched && tenantQuery.data) {
      const metadataTypes = tenantQuery.data.tenantDocumentMetadataList || [];

      const mappedData = metadataTypes.map((item) => {
        let type: "string" | "number" | "boolean" | "dateTime" | "select" =
          "string";

        switch (item.metadataTypeEnum) {
          case MetadataEnum.StringValue:
            type = "string";
            break;
          case MetadataEnum.NumberValue:
            type = "number";
            break;
          case MetadataEnum.BooleanValue:
            type = "boolean";
            break;
          case MetadataEnum.DateTimeValues:
            type = "dateTime";
            break;
          case MetadataEnum.SelectValues:
            type = "select";
            break;
          default:
            type = "string";
        }

        return {
          id: item.id,
          name: item.name,
          externalPropertyName: item.externalPropertyName,
          type,
          selectValues: item.selectValues || [],
        };
      });

      const formData = {
        metadataTypes: mappedData,
      };
      form.reset(formData);
      setOriginalData(formData);
    }
  }, [tenantQuery.data, tenantQuery.isFetched, form]);

  const addNewMetadataType = () => {
    append({
      name: "",
      externalPropertyName: undefined,
      type: "string",
    });
  };

  const handleSave = async (values: FormValues) => {
    try {
      if (!tenantQuery.data?.id) throw new Error("Tenant ID is required");

      // Helper to check duplicates and set form errors
      const checkDuplicates = (
        items: TenantMetadataFormType[],
        key: keyof TenantMetadataFormType,
        errorMsg: string
      ) => {
        const names = items
          .map((item) => {
            const value = item[key];
            if (Array.isArray(value)) return ""; // Ignore arrays
            return value?.toLowerCase().trim() ?? "";
          })
          .filter((name) => name.length > 0);

        const counts = names.reduce(
          (acc, name) => {
            acc[name] = (acc[name] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>
        );

        let hasErrors = false;
        items.forEach((item, index) => {
          const value = item[key];
          if (Array.isArray(value)) return; // skip arrays
          const normalized = value?.toLowerCase().trim();
          if (normalized && counts[normalized] > 1) {
            form.setError(`metadataTypes.${index}.${key}`, {
              type: "manual",
              message: errorMsg,
            });
            hasErrors = true;
          }
        });

        return hasErrors;
      };

      if (
        checkDuplicates(values.metadataTypes, "name", "Navnet skal være unikt")
      )
        return;

      if (
        checkDuplicates(
          values.metadataTypes,
          "externalPropertyName",
          "Dokument egenskaben skal være unik"
        )
      )
        return;

      // Check select values
      const selectErrors = values.metadataTypes.some((item, index) => {
        if (
          item.type === "select" &&
          (!item.selectValues || item.selectValues.length === 0)
        ) {
          form.setError(`metadataTypes.${index}.selectValues`, {
            type: "manual",
            message: "Der skal mindst være én valgmulighed",
          });
          return true;
        }
        return false;
      });

      if (selectErrors) return;

      // Helper to determine if an item has changed
      const hasItemChanged = (
        current: TenantMetadataFormType,
        original?: TenantMetadataFormType
      ) => {
        if (!current.id) return true;
        if (!original) return true;

        const normalize = (item: TenantMetadataFormType) => ({
          ...item,
          externalPropertyName: normalizeExternalPropertyName(
            item.externalPropertyName
          ),
          selectValues: item.selectValues || [],
        });

        const currNorm = normalize(current);
        const origNorm = normalize(original);

        return (
          currNorm.name !== origNorm.name ||
          currNorm.type !== origNorm.type ||
          currNorm.externalPropertyName !== origNorm.externalPropertyName ||
          JSON.stringify(currNorm.selectValues) !==
            JSON.stringify(origNorm.selectValues)
        );
      };

      // Filter items to update
      const itemsToUpdate = values.metadataTypes.filter((item) => {
        if (!originalData) return true;
        const originalItem = originalData.metadataTypes.find(
          (o) => o.id === item.id
        );
        return hasItemChanged(item, originalItem);
      });

      if (itemsToUpdate.length === 0) {
        showSuccessToast("Ingen ændringer at gemme", "Metadata type");
        return;
      }

      // Map to tenant metadata
      const tenantMetadataList = itemsToUpdate.map((item) => ({
        id: item.id,
        name: item.name,
        externalPropertyName: normalizeExternalPropertyName(
          item.externalPropertyName
        ),
        metadataTypeEnum: mapTypeToEnum(item.type),
        selectValues: item.selectValues,
      }));

      await updateTenantMetadata.mutateAsync({
        tenantId: tenantQuery.data.id,
        tenantMetadataList,
      });

      setOriginalData(values);
      showSuccessToast("Metadata typerne er blevet gemt", "Metadata type");
    } catch (error) {
      logger.error("Failed to update tenant metadata types", {
        operation: "updateTenantMetadata",
        error,
        tenantId: tenantQuery.data?.id,
      });
      showErrorToast(
        "Der skete en fejl ved opdatering - prøv igen.",
        "Metadata type"
      );
    }
  };

  // Utility to map type to enum
  const mapTypeToEnum = (type: string): MetadataEnum => {
    switch (type) {
      case "string":
        return MetadataEnum.StringValue;
      case "number":
        return MetadataEnum.NumberValue;
      case "boolean":
        return MetadataEnum.BooleanValue;
      case "dateTime":
        return MetadataEnum.DateTimeValues;
      case "select":
        return MetadataEnum.SelectValues;
      default:
        return MetadataEnum.StringValue;
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setIsDeletingMetadata(id);
      await deleteTenantMetadata.mutateAsync({ tenantMetadataId: id });
      showSuccessToast("Metadata type blev slettet", "Metadata type");
    } catch (error) {
      logger.error("Failed to delete tenant metadata type", {
        operation: "deleteTenantMetadata",
        error,
        metadataTypeId: id,
      });
      showErrorToast(
        "Der skete en fejl ved sletning - prøv igen.",
        "Metadata type"
      );
    } finally {
      setIsDeletingMetadata(undefined);
    }
  };

  const handleReset = () => {
    if (originalData) {
      form.reset(originalData);
    }
  };

  const watchedValues = form.watch();

  // Normalize form values to handle empty strings vs undefined/null
  const normalizedWatchedValues = useMemo(() => {
    if (!watchedValues.metadataTypes) return watchedValues;

    return {
      ...watchedValues,
      metadataTypes: watchedValues.metadataTypes.map((item) => ({
        ...item,
        externalPropertyName: normalizeExternalPropertyName(
          item.externalPropertyName
        ),
        selectValues: item.selectValues || [],
      })),
    };
  }, [watchedValues]);

  // Check if form has changes - make it reactive
  const hasChanges = useMemo(() => {
    if (!originalData) return false;

    // Deep comparison function for arrays of objects
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deepEqual = (a: any, b: any): boolean => {
      if (a === b) return true;
      if (a == null || b == null) return false;
      if (typeof a !== typeof b) return false;

      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        return a.every((item, index) => deepEqual(item, b[index]));
      }

      if (typeof a === "object") {
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        if (keysA.length !== keysB.length) return false;
        return keysA.every((key) => {
          const valA = a[key];
          const valB = b[key];

          // Handle externalPropertyName normalization
          if (key === "externalPropertyName") {
            return deepEqual(
              normalizeExternalPropertyName(valA),
              normalizeExternalPropertyName(valB)
            );
          }

          return deepEqual(valA, valB);
        });
      }

      return false;
    };

    const hasChangesResult = !deepEqual(normalizedWatchedValues, originalData);

    return hasChangesResult;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedWatchedValues, originalData, form.formState]);

  return (
    <PageContent title="Metadata">
      <div className="flex flex-col h-full w-full space-y-6 p-4 xl:px-8">
        <div className="flex items-center justify-between pb-4 px-2 border-b mb-5">
          <div>
            <h2 className="text-xl font-bold">Metadata</h2>
            <p className="text-muted-foreground">
              Administrer metadata for dine dokumenter
            </p>
          </div>
        </div>

        {tenantQuery.isPending ? (
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col gap-2 h-[calc(100vh-14rem)] overflow-y-auto px-2">
              {Array.from({ length: 16 }).map((_, index) => (
                <MetadataTypesSkeleton key={index} />
              ))}
            </div>
            <div className="flex gap-2 px-2">
              <Skeleton className="h-9 w-39" />
              <Skeleton className="h-9 w-22" />
              <Skeleton className="h-9 w-48" />
            </div>
          </div>
        ) : (
          <Form {...form}>
            <div className="space-y-4">
              <MetadataFormContent
                fields={fields}
                form={form}
                handleDelete={handleDelete}
                isDeletingMetadata={isDeletingMetadata}
                isTenantLoading={tenantQuery.isPending}
              />
              <MetadataFormFooter
                addNewMetadataType={addNewMetadataType}
                fields={fields}
                form={form}
                hasChanges={hasChanges}
                handleReset={handleReset}
                onSaveClick={() => form.handleSubmit(handleSave)()}
              />
            </div>
          </Form>
        )}
        <ConfirmationDialog
          isOpen={isResetDialogOpen}
          onClose={() => setIsResetDialogOpen(false)}
          onConfirm={handleReset}
          title="Nulstil alle ændringer"
          description="Alle ændringer bliver fjernet, og alt data bliver nulstillet til den oprindelige tilstand."
          confirmText="Nulstil alle ændringer"
        />
      </div>
    </PageContent>
  );
};

export default MetadataTypesView;
